
class AweMap extends HTMLElement {
  // Specify observed attributes so that attributeChangedCallback will work
  static get observedAttributes() {
    return ['visible']
  }

  get lang() {
    return this._lang
  }
  set lang(value) {
    // TODO: if change, trigger map layers rename
    this._lang = value.substring(0, 2)
    this.setAttribute('lang', this._lang)

    return this._lang // eslint-disable-line no-setter-return
  }

  get avails() {
    return this._avails
  }
  set avails(value) {
    this._avails = value

    return this._avails // eslint-disable-line no-setter-return
  }

  get fitToArea() {
    return this._fitToArea
  }
  set fitToArea(value) {
    this._fitToArea = value || 0
    this.setAttribute('fit-to-area', this._fitToArea)

    return this._fitToArea // eslint-disable-line no-setter-return
  }

  get clusters() {
    return this._clusters
  }
  set clusters(value) {
    this._clusters = !!value
    if (this._clusters)
      this.setAttribute('clusters', '')
    else
      this.removeAttribute('clusters')

    return this._clusters // eslint-disable-line no-setter-return
  }

  get currentAvail() {
    return this._currentAvail
  }
  set currentAvail(value) {
    this._currentAvail = value
    // assign selected class to the correct marker node
    const curNd = this.shadowRoot.querySelector('.store-marker.selected')

    if (curNd)
      curNd.classList.remove('selected')

    if (value) {
      const newSel = this.shadowRoot.querySelector(`.store-marker[data-sid='${this._currentAvail.store.id}']`)

      if (newSel)
        newSel.classList.add('selected')

      if (this.clusters) {
        // fit only if map is at or below default zoom (i.e. after init).
        // Otherise do not fit the map to content, so as to not change the zoom level.
        const fit = this._map && this._map.getZoom() <= this._DEFAULT_ZOOM

        this._setClusters(fit)
      }
    }

    return this._currentAvail // eslint-disable-line no-setter-return
  }

  set mapMoved(value) {
    if (value != this._mapMoved)
      this.shadowRoot.getElementById('map-messages').classList.toggle('hidden', !value)

    this._mapMoved = value

    return this._mapMoved // eslint-disable-line no-setter-return
  }

  get _userPos() { // eslint-disable-line getter-return
    if (this._userLocation)
      return [this._userLocation.lng, this._userLocation.lat]
  }
  get userLocation() {
    return this._userLocation
  }


  constructor() {
    super()

    this._MAPBOX_SCRIPT = 'https://api.tiles.mapbox.com/mapbox-gl-js/v1.12.0/mapbox-gl.js'
    this._MAPBOX_STYLE = 'https://api.tiles.mapbox.com/mapbox-gl-js/v1.12.0/mapbox-gl.css'
    this._MIN_DISTANCE = 500 // minimum distance in meters to be new a location
    this._DEFAULT_ZOOM = 10

    this.styleUri = 'mapbox://styles/mapbox/streets-v10'
    this._storePictoUrl = 'https://content.static-swaven.com/img/picto_map/[[SID]].png'

    // promise returned by mapbox load & init wrapper is kept,
    // so that we can debounce multiple calls to show() and do the load/init only once.
    this._mapboxPromise = null
    this._mapCreated = false
    this._map = null // mapbox object
    this._userMarker = null
    this._storeMarkers = new Map() // maps each avail (via store id) with its marker object
    this._missedAvailsUpdate = false // whether avails list was updated before map load & init

    this._userLocation = null
    this._avails = [] // all availabilities to show
    this._currentAvail = null // currently selected availability
    this._lang = 'en'
    this._fitToArea = 0
    this._mapPadding = { top: 50, left: 30, right: 30, bottom: 15 }
    this._mapMoved = false // whether use dragged the map
    this._clusters = false // whether to use clustering
    this._unclusterDebouncer = -1 // timeout id to debounce showing unclustered avails
    this._unclusteredStoreIds = null // list of store ids to exlude from clustering
    this.userMarkerColor = '#f39027'
    this.clusterRadius = 20
    this.clusterColor = '#51bbd6'
    this.clusterTextColor = '#000'
    this.clusterExclusionTags = null // list of tags (strings). Stores with these tags are unclustered.
    this.messagePosition = 'top'
    this.routeColor = '#2d334e'
    this.visible = false

    /* set props from attributes */

    this.attachShadow({ mode: 'open' })

    if (this.hasAttribute('lang'))
      this.lang = this.getAttribute('lang')
    else
      this.setAttribute('lang', this.lang)

    if (this.hasAttribute('user-marker-color'))
      this.userMarkerColor = this.getAttribute('user-marker-color')
    else
      this.setAttribute('user-marker-color', this.userMarkerColor)

    if (this.hasAttribute('fit-to-area'))
      this.fitToArea = parseInt(this.getAttribute('fit-to-area'), 10) || 0
    else
      this.setAttribute('fit-to-area', this.fitToArea)

    if (this.hasAttribute('style-uri'))
      this.styleUri = this.getAttribute('style-uri')
    else
      this.setAttribute('style-uri', this.styleUri)

    this.clusters = this.hasAttribute('clusters')

    // cluster options are serialized only if clusters are enabled.

    if (this.hasAttribute('cluster-radius'))
      this.clusterRadius = parseInt(this.getAttribute('cluster-radius'), 10) || 20
    else if (this.clusters)
      this.setAttribute('cluster-radius', this.clusterRadius)

    if (this.hasAttribute('cluster-color'))
      this.clusterColor = this.getAttribute('cluster-color')
    else if (this.cluster)
      this.setAttribute('cluster-color', this.clusterColor)

    if (this.hasAttribute('cluster-text-color'))
      this.clusterTextColor = this.getAttribute('cluster-text-color')
    else if (this.clusters)
      this.setAttribute('cluster-text-color', this.clusterTextColor)

    if (this.hasAttribute('cluster-exclusion-tags'))
      this.clusterExclusionTags = this.getAttribute('cluster-exclusion-tags').split(',').map(x => x.trim()).filter(x => !!x)

    if (this.hasAttribute('message-position'))
      this.messagePosition = this.getAttribute('message-position')

    if (this.hasAttribute('route-color'))
      this.routeColor = this.getAttribute('route-color')

    /* Build DOM */

    // container node for mapbox instance
    const ctnr = document.createElement('div')

    ctnr.id = '_map-ctnr'

    // messages container
    const msgCtnr = document.createElement('div')

    msgCtnr.id = 'map-messages'
    msgCtnr.classList.add(this.messagePosition, 'hidden')
    const mapMovedBtn = document.createElement('button')

    mapMovedBtn.classList.add('map-moved-btn')
    mapMovedBtn.textContent = i18n.get('AWE-MAP.UPDATE_LOCATION')
    mapMovedBtn.addEventListener('click', e =>  { this._updateLocation(e) })
    msgCtnr.appendChild(mapMovedBtn)

    const style = document.createElement('style')

    style.textContent = `:host{display:block;width:100%;height:100%;background:#ddd;overflow:hidden;color:#000;border:none}#_map-ctnr{width:100%;height:100%;z-index:1}.store-marker{width:29px;height:35px}.hidden{opacity:0!important;z-index:0!important}#map-messages{position:absolute;left:50%;transform:translateX(-50%);opacity:1;transition:opacity .4s;z-index:1}.map-moved-btn{border-radius:4px;color:#fff;background:#000;cursor:pointer;padding:.8em 1.2em;white-space:nowrap}#map-messages>:first-child{margin-bottom:6px}#map-messages button{border:none}.top{top:30px}.bottom{bottom:0}`
    this.shadowRoot.append(style, ctnr, msgCtnr)

    document.addEventListener('location-update', async (e) => {
      this._userLocation = e.detail.location

      if (this._mapboxPromise != null) {
        await this._mapboxPromise

        if (this._userMarker)
          this._userMarker.setLngLat(this._userPos)
        else
          this._createUserMarker()
      }
      else if (this.visible && this.isConnected) {
        await this.show(null, false)
      }
    })

    document.addEventListener('availabilities-update', e => {
      // use only avails with a physical location
      // TODO: support attribute that configures which list to use
      const realAvails = e.detail.avails.filter(x =>
        x.store.brick || x.store.collect || x.store.drive)

      this.avails = realAvails

      if (this.currentAvail) {
        const selInNewAvails = this.avails.find(x => x.store.id === this.currentAvail.store.id)

        if (selInNewAvails)
          this.currentAvail = selInNewAvails
        else
          this.currentAvail = null
      }

      if (this.clusters)
        this._setClusters()
      else
        this._setStoreMarkers()

      this._clearRoute()
    })

    const storeMarkerTmpl = document.createElement('template')

    storeMarkerTmpl.id = 'store-marker-tmpl'
    storeMarkerTmpl.innerHTML = '<div class="store-marker" tabindex="0">'
    this.shadowRoot.append(storeMarkerTmpl)
  }

  async show(avail, showRoute = false, auto = false) {
    try {
      // loads only once, but always wait for promise.
      if (this._mapboxPromise == null)
        this._mapboxPromise = this._load()

      await this._mapboxPromise
    }
    catch (err) {
      logger.error('Mapbox load error', err)
      throw new Error('Cannot load Mapbox')
    }

    if (avail) {
      // if availability has a nearestStore value, replace store object with nearestStore, since it's those values we want to see on a map.
      if (avail.nearestStore) {
        avail.store = avail.nearestStore
        avail.nearestStore = null

        // if nearest store is closed for lockdown, the infowindow should show it.
        // Since lockdown prop is on the availability and not on the store prop, we need to find the availability that gave its store and use its lockdown value
        const nearest = this.avails.find(x => x.store.id === avail.store.id)

        if (nearest)
          avail.lockdown = nearest.lockdown
      }

      const newSelection = this.currentAvail && (this.currentAvail.store.id !== avail.store.id)

      this.currentAvail = avail

      if (showRoute)
        this._getDirections(null, !!auto)
      else {
        // clear route only if selected store is different from the previous selection
        if (newSelection)
          this._clearRoute()

        // center map on store
        if (avail.store && avail.store.location) {
          this._map.flyTo({
            center: [avail.store.location.longitude, avail.store.location.latitude],
          })
        }

        // if (!this.noPopup)
        //   this._showPopup()
      }
    }

    if (this._missedAvailsUpdate) {
      if (this.clusters)
        this._setClusters()
      else
        this._setStoreMarkers()
    }
    // if an availability is selected with clusters enabled and selected store only option (default or expanded) is enabled,
    // refresh cluster source. That's because with these options, cluster data source consists only of selected store.
    else if (this._currentAvail && this.clusters && this.defaultViewSelectedOnly && !this.expanded || this.expandedViewSelectedOnly && this.expanded) // eslint-disable-line max-len
      this._setClusters()
  }

  // Mapbox setup wrapper. Loads script, creates map and initializes it.
  async _load() {
    await this._loadScript()
    await this._createMap()
    this._initMap()

    const duration = performance.getEntriesByName('mapbox-load-time', 'measure').pop().duration

    logger.log('mapbox-load', { duration: Math.round(duration) })
  }

  // loads mapbox script & style
  async _loadScript() {
    return new Promise((resolve, reject) => {
      const nd = document.createElement('script')
      const styleNd = document.createElement('link')

      nd.src = this._MAPBOX_SCRIPT

      nd.onload = () => {
        performance.measure('mapbox-load-time', 'mapbox-load-start')

        return resolve()
      }
      nd.onerror = () => reject('Error loading Mapbox script')

      styleNd.href = this._MAPBOX_STYLE
      styleNd.setAttribute('rel', 'stylesheet')

      performance.mark('mapbox-load-start')
      this.shadowRoot.append(nd, styleNd)
    })
  }

  // creates mapbox instance
  _createMap() {
    return new Promise((resolve) => {
      mapboxgl.accessToken = 'pk.eyJ1Ijoic3dhdmVuIiwiYSI6ImNqbTd1dXBlcjFhejMzcHBvajVlYjFjMXcifQ.ljVSrON2CjvnSj38Nxa9RA'
      this._map = new mapboxgl.Map({
        container: this.shadowRoot.getElementById('_map-ctnr'),
        style: this.styleUri,
        center: this._userPos,
        zoom: this._DEFAULT_ZOOM,
      })
      this._map.on('load', resolve)
    })
  }

  // sets up basic config
  _initMap() {
    const mapboxStyle = this._map.getStyle()

    // We have to change manually the language for all layers.
    // Only change text fields and avoid changing road_label (ex: A1, D15, N25) because it's messing and useless
    mapboxStyle.layers.forEach(layer => {
      if (layer.layout && layer.layout['text-field'] && layer['source-layer'] != 'road_label') {
        this._map.setLayoutProperty(layer.id, 'text-field', ['get', 'name_' + this.lang])
      }
    })

    this._map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'bottom-right')

    if (this.clusters) {
      this._initClusters()
    }

    if (this._userPos?.[0] != null && this._userPos?.[1] != null)
      this._createUserMarker()
    else
      console.warn('Map init: user position not set')  // eslint-disable-line no-console

    this._map.on('dragend', () => {
      const center = this._map.getCenter()

      if (this.getHaversineDistance(this._userPos, center) > this._MIN_DISTANCE )
        this.mapMoved = true
    })
    this.dispatchEvent(new CustomEvent('map-init'))
    this._mapCreated = true
  }

  // TODO: user marker slot
  _createUserMarker() {
    // this id is added to the node with attribute slot=user-marker by preprocessor
    const userMarkerContent = document.getElementById('user-marker'),
      markerOptions = {}

    if (userMarkerContent) {
      markerOptions.element = userMarkerContent
      const anchor = userMarkerContent.getAttribute('anchor')

      if (anchor)
        markerOptions.anchor = anchor
    }
    else
      // default marker, custom color
      markerOptions.color = this.userMarkerColor

    // create, place and add marker to map
    this._userMarker = new mapboxgl.Marker(markerOptions)
    this._userMarker.setLngLat(this._userPos)
    this._userMarker.addTo(this._map)
  }

  // shows markers for stores.
  // storeIds: list of store ids to display. If not provided, show all stores
  // fit: whether to adjust map viewport to show all markers
  _setStoreMarkers(storeIds, fit = true) {
    if (!this._mapCreated) {
      this._missedAvailsUpdate = true

      return
    }
    this._missedAvailsUpdate = false

    // Clear old markers if no avails
    if (!this.avails || !this.avails.length) {
      for (const key of this._storeMarkers.keys()) {
        const marker = this._storeMarkers.get(key)

        this._storeMarkers.delete(key)
        marker.remove()
      }

      return
    }

    let stores

    if (storeIds)
      stores = this.avails.filter(x => storeIds.indexOf(x.store.id) > -1)
    else {
      stores = this.avails
      storeIds = stores.map(x => x.store.id)
    }

    // find store ids no longer in store list & remove associated markers from the map
    for (const key of this._storeMarkers.keys()) {
      if (!storeIds.includes(key)) {
        const marker = this._storeMarkers.get(key)

        this._storeMarkers.delete(key)
        marker.remove()
      }
    }

    stores.forEach(s => {
      // creates a marker if none exists for the store
      if (!this._storeMarkers.has(s.store.id))
        this._createMarker(s)
    })

    if (fit)
      this._fitMap()
  }

  // creates a marker for the given availability
  // TODO: store marker slot
  async _createMarker(avail) {
    const markerNd = this.shadowRoot.getElementById('store-marker-tmpl').content.firstElementChild.cloneNode()

    const logoId = player.store.showRetailerText ? 'default' : avail.retailer.logoId

    markerNd.style['background-image'] = `url('${this._storePictoUrl.replace('[[SID]]', logoId)}')`
    markerNd.dataset.sid = avail.store.id // keep store id to allow retrieving the related store in list
    // // if (this.selectedRetailerOnly)
    // //   markerNd.classList.add('r-' + store.retailer.id)
    markerNd.addEventListener('click', e => { this._markerClickHandler(e) })
    markerNd.addEventListener('keypress', e => { this._markerClickHandler(e) })

    if (this.currentAvail && this.currentAvail.store.id === avail.store.id)
      markerNd.classList.add('selected')

    const marker = new mapboxgl.Marker({
      element: markerNd,
      anchor: markerNd.getAttribute('anchor') || 'bottom',
    })

    marker.setLngLat([avail.store.location.longitude, avail.store.location.latitude])
    marker.addTo(this._map)
    this._storeMarkers.set(avail.store.id, marker)
  }

  // TODO: handle pop-up
  _markerClickHandler(e) {
    e.stopPropagation()

    if (e.type === 'keypress' & e.key !== 'Enter')
      return

    e.currentTarget.focus()

    const sid = e.currentTarget.dataset.sid,
      avail = this.avails.find(x => x.store.id === sid)

    if (!sid || !avail)
      return console.error(`Store ${sid} could not be found.`) // eslint-disable-line no-console

    this.currentAvail = avail
    // this._styleSelectedMarker(e.currentTarget)

    // if (!this.noPopup)
    //   this._showPopup()

    // if (this.reduceOnSelect && this.expanded)
    //   this.toggleExpand()

    this.dispatchEvent(new CustomEvent('availability-select', { detail: { avail, auto: false }, bubbles: false }))
    // Tracker.dropTag('clkloc', {avail: this.selected, area: Area.MAP})
  }

  // Supports only fitting all avails. TODO: fit-to-stores
  _fitMap() {
    // trying to fit with no stores fits only the user location, i.e. just a max zoom on the position.
    // Instead, we center the map on user position with a bounding box if provided, or a predefined zoom.
    if (this.avails.length === 0) {
      if (this._userLocation.boundingBox) {
        this._map.fitBounds(this._userLocation.boundingBox)
      }
      else {
        this._map.flyTo({
          center: this._userPos,
          zoom: 9,
        })
      }

      return
    }

    // force zoom to location boundingbox if enough stores in it or if it's a country (& option is enabled and boundingbox exists)
    if (this.fitToArea && this._userLocation.boundingBox
        && (this.countStoresInArea(this._userLocation.boundingBox, this.avails) >= this.fitToArea || this._userLocation.type === 'country')) {
      // fit to provided box
      this._map.fitBounds(this._userLocation.boundingBox, { padding: 10 })
    }
    else {
      // fit to stores (all or subset)

      // fit to stores: fit only the N closest stores
      // if (this.fitToStores || fitCount){
      //   const count = fitCount || this.fitToStores
      //   stores = this.stores.map(x => x ) // create a new array, so the sort does not modify the source property
      //             .sort((a, b) => a.store.distance - b.store.distance) // sort by distance
      //             .slice(0, count) // take only the requested number

      //   if (!this.selected.isDefault) // if a store is selected, include it in bounding box
      //     stores.push(this.selected)
      // }
      // else
      const stores = this.avails

      // boundaries object is extended by adding positions that must be within the boundaries
      const bounds = new mapboxgl.LngLatBounds()

      for (let i = 0; i < stores.length; i++) {
        const loc = stores[i].store.location

        // exclude stores without geoloc from boundaries
        if (loc.latitude != 0 || loc.longitude != 0)
          bounds.extend([loc.longitude, loc.latitude])
      }

      if (this._userPos)
        bounds.extend(this._userPos)

      // dynamic padding is not implemented.
      /* if (this._mapPadding == null){
         this._computePadding()
       }*/

      // delay fitBounds, otherwise it does not work at init
      setTimeout(() => {
        this._map.fitBounds(bounds, { padding: this._mapPadding })
      }, 1)
    }
  }

  // show markers for unclustered stores.
  // debounce to avoid doing the work too often.
  // Results are deduplicated since features returned by querySourceFeatures can contain duplicates.
  _showUnclustered() {
    if (this._unclusterDebouncer !== -1)
      clearTimeout(this._unclusterDebouncer)

    this._unclusterDebouncer = setTimeout(() => {
      this._unclusterDebouncer = -1
      const singleStores = this._map.querySourceFeatures('stores', { filter: ['!has', 'point_count'] })
        .map(x => x.properties.sid)
        .concat(this._unclusteredStoreIds) // add cluster-excluded stores to the list
        .reduce((dedups, feat) => {
          if (!dedups.includes(feat))
            dedups.push(feat)

          return dedups
        }, [])

      this._setStoreMarkers(singleStores, false)
      // setTimeout(this._styleSelectedMarker, 50)
    }, 100)
  }

  // Sort stores between the ones to cluster and the ones to exclude.
  // Returns list of stores to cluster. Unclustered store ids are in unclusteredStoreIds property. Stores to hide completely are discarded.
  _sortStoresForClustering() {
    const clusteredStores = []
    const unclusteredIds = []

    this.avails.forEach(s => {

      // if option selected-retailer-only is enabled, stores from retailers other than current selection's retailer are discarded
      if (this.selectedRetailerOnly
        && this.currentAvail.retailer.id
        && this.currentAvail.retailer.id !== s.retailer.id
      )
        return

      // stores with no tags are included
      if (!s.store.tags || s.store.tags.length === 0) {
        clusteredStores.push(s)

        return
      }

      // find if store's tag array contains an exclusion tag
      let hasExclTag = false, i = 0

      while (!hasExclTag && i < s.store.tags.length) {
        if (this.clusterExclusionTags.indexOf(s.store.tags[i++]) > -1)
          hasExclTag = true
      }

      if (hasExclTag)
        unclusteredIds.push(s.store.id)
      else
        clusteredStores.push(s)
    })

    return { clusteredStores, unclusteredIds }
  }

  // sets the data source used to show store clusters
  _setClusters(fit = true) {
    if (!this._mapCreated) {
      this._missedAvailsUpdate = true

      return
    }

    const src = this._map.getSource('stores')

    if (!this._mapCreated || !src) {
      this._missedAvailsUpdate = true

      return
    }
    this._missedAvailsUpdate = false

    let clusteredStores // list of stores to add to cluster datasource

    this._unclusteredStoreIds = []

    // TODO: most of the properties in the tests below are not defined.
    //       If supported, they should exist as public props & attributes.
    if (this.clusterExclusionTags || this.selectedRetailerOnly) {
      // get list of stores to cluster & list of ids to exclude from clustering
      const sortResults = this._sortStoresForClustering()

      clusteredStores = sortResults.clusteredStores
      this._unclusteredStoreIds = clusteredStores.unclusteredIds
    }
    // take only selected if option is enabled
    else if (this.defaultViewSelectedOnly && !this.expanded || this.expandedViewSelectedOnly && this.expanded) // eslint-disable-line max-len
      clusteredStores = [this.currentAvail]
    else
      // no exclusion tag defined, add all stores
      clusteredStores = this.avails

    // remove selected store from cluster dataset, and add it to unclustered stores
    if (this.currentAvail) {
      clusteredStores = clusteredStores.filter(s => s.store.id !== this.currentAvail.store.id)
      if (!this._unclusteredStoreIds)
        this._unclusteredStoreIds = []
      this._unclusteredStoreIds.push(this.currentAvail.store.id)
    }

    // set stores source data
    src.setData({
      type: 'FeatureCollection',
      features: clusteredStores.map(x => {
        return {
          type: 'Feature',
          properties: {
            name: x.store.name,
            rid: x.retailer.id,
            lat: x.store.location.latitude,
            lng: x.store.location.longitude,
            sid: x.store.id,
          },
          geometry: {
            type: 'Point',
            coordinates: [x.store.location.longitude, x.store.location.latitude],
          },
        }
      }),
    })

    if (fit)
      this._fitMap()
  }

  // set up clusters configuration: datasource, layer, event handling
  _initClusters() {
    // create empty datasource for store locations
    this._map.addSource('stores', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
      cluster: true,
      clusterMaxZoom: 13,
      clusterRadius: 50,
    })

    // cluster layer
    this._map.addLayer({
      id: 'clusters',
      source: 'stores',
      type: 'circle',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': this.clusterColor,
        'circle-radius': this.clusterRadius,
      },
    })

    // cluster count layer
    this._map.addLayer({
      id: 'cluster-count',
      type: 'symbol',
      source: 'stores',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['Arial Unicode MS Bold'],
        'text-size': 12,
      },
      paint: {
        'text-color': this.clusterTextColor,

      },
    })

    // inspect a cluster on click
    this._map.on('click', 'clusters', e => {
      const features = this._map.queryRenderedFeatures(e.point, { layers: ['clusters'] })
      const clusterId = features[0].properties.cluster_id

      this._map.getSource('stores').getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return

        this._map.easeTo({
          center: features[0].geometry.coordinates,
          zoom: zoom,
        })
      })
    })

    // attach handler to all events that change clusters or make an unclustered store show/hide
    this._map.on('zoomend', () => { this._showUnclustered() })
    this._map.on('dragend', () => { this._showUnclustered() })
    this._map.on('sourcedata', e => {
      if (e.sourceId === 'stores')
        this._showUnclustered()
    })
  }

  async _updateLocation(evt) {
    evt.stopPropagation()
    this.mapMoved = false // reset even if the location is ignored by app.

    const center = this._map.getCenter(),
      // don't filter results on enabled countries, we want to get location wherever it is
      location = await player.getReverseGeocoding(center.lng, center.lat, [], this.lang)

    if (!location) {
      console.error('Could not get address from coordinates') // eslint-disable-line no-console

      return
    }

    this.dispatchEvent(new CustomEvent('location-update', { detail: { location, auto: false } }))
    // if (this.countries.includes(loc.country) || (this.allowOos & OosLevels.ALLOW) === OosLevels.ALLOW){
    //   this.$emit('location-update', loc)
    //   this.internalError = ''
    // }
    // else
    //   this.internalError = ErrorCodes.UNAVAILABLE_COUNTRY_ERROR
  }

  async _getDirections(e) {
    if (e && e.stopPropagation)
      e.stopPropagation()

    const query = `${this._userPos[0]},${this._userPos[1]};${this.currentAvail.store.location.longitude},${this.currentAvail.store.location.latitude}`

    try {
      const res = await fetch(`https://api.mapbox.com/directions/v5/mapbox/driving/${query}?geometries=geojson&access_token=pk.eyJ1Ijoic3dhdmVuIiwiYSI6ImNqbTd1dXBlcjFhejMzcHBvajVlYjFjMXcifQ.ljVSrON2CjvnSj38Nxa9RA`),
        json = await res.json(),
        route = json.routes[0].geometry

      this._clearRoute()

      this._map.addLayer({
        id: 'route',
        type: 'line',
        source: {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: route,
          },
        },
        paint: {
          'line-width': 3,
          'line-color': this.routeColor,
        },
      })

      // taken straight from https://www.mapbox.com/mapbox-gl-js/example/zoomto-linestring/
      const bounds = route.coordinates.reduce((bounds, coord) => {
        return bounds.extend(coord)
      }, new mapboxgl.LngLatBounds(route.coordinates[0], route.coordinates[0]))

      this._map.fitBounds(bounds, { padding: this._mapPadding })

      if (this._popup)
        this._popup.remove()
    }
    catch (err) {
      console.error(err) // eslint-disable-line no-console
    }
    finally {
      // track mapbox usage
      tracker.track('mapbox-request', { api: 'directions', query }, TARGET_LOG)
    }
    tracker.track('clkRoute', { avail: this.currentAvail, area: 'map', auto: false })
  }

  _clearRoute() {
    const routeLayer = this._map && this._map.getLayer('route')

    if (routeLayer) {
      this._map.removeLayer('route') // Remove route drawn
      this._map.removeSource('route') // Remove source related to this layer
    }
  }

  // return the number of stores that are inside the provided bounding box
  countStoresInArea(bbox, avails) {
    const minLng = bbox[0],
      minLat = bbox[1],
      maxLng = bbox[2],
      maxLat = bbox[3]

    const count = avails.reduce((count, avail) => {
      if (minLat <= avail.store.location.latitude && avail.store.location.latitude <= maxLat
          && minLng <= avail.store.location.longitude && avail.store.location.longitude <= maxLng)
        return count+1
      else
        return count
    }, 0)

    return count
  }

  // Returns the distance in meters between 2 coordinates.
  // loc1, loc2: objects|arrays. If objects, they must have latitude (or lat) and longitude (or lng) properties.
  // If arrays, index 0 is longitude, index 1 is latitude.
  // loc1 can be an object and loc2 an array (or vice-versa)
  getHaversineDistance(loc1, loc2) {
    const earthDiameter = 12742 * 1000, // in metres
      lng1 = this.toRadians(loc1.longitude || loc1.lng || loc1[0] || 0),
      lat1 = this.toRadians(loc1.latitude || loc1.lat || loc1[1] || 0),
      lng2 = this.toRadians(loc2.longitude || loc2.lng || loc2[0] || 0),
      lat2 = this.toRadians(loc2.latitude || loc2.lat || loc2[1] || 0),

      havLat = Math.sin((lat2 - lat1) / 2 ) ** 2,
      havLng = Math.sin((lng2 - lng1) / 2 ) ** 2,
      h = Math.min(havLat + havLng * Math.cos(lat1) * Math.cos(lat2), 1) // h cannot be > 1

    return earthDiameter * Math.asin(Math.sqrt(h))
  }

  // convert degrees to radians
  toRadians(val) {
    return val * Math.PI / 180
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'visible')
      this.visible = newValue !== null && newValue !== 'false'

    if (this.visible
      && this.isConnected
      && this._userPos?.[0] != null && this._userPos?.[1] != null) {

      map.show()
    }
  }

  connectedCallback() {
    if (!this.isConnected) return

    if (this.hasAttribute('visible'))
      this.visible = this.getAttribute('visible') !== 'false'
    else
      this.setAttribute('visible', this.visible.toString())

    if (this.visible
      && this.isConnected
      && this._userPos?.[0] != null && this._userPos?.[1] != null) {

      map.show()
    }
  }
}

customElements.define('awe-map', AweMap)

// public helper function. No need to put in player, this way it's only present when needed.
function showMap(evt, avail, showRoute = false, auto = false) { // eslint-disable-line no-unused-vars
  if (evt)
    evt.stopPropagation()

  if (avail) {
    player.store.currentAvail = avail
    if (!auto)
      tracker.track('clkloc', { auto, avail })
  }

  const map = document.getElementsByTagName('awe-map')[0]

  if (map)
    map.show(avail, showRoute)
}

// default event handlers for the element
const mapNd = document.getElementsByTagName('awe-map')[0]

if (mapNd) {
  mapNd.addEventListener('location-update', e => {
    player.store.location = e.detail.location
    tracker.track('geoloc', { auto: !!e.detail.auto })
  })
  mapNd.addEventListener('availability-select', e => {
    player.store.currentAvail = e.detail.avail
    tracker.track('clkloc', { avail: e.detail.avail, auto: !!e.detail.auto })
  })
}
